import styled, { keyframes, css } from 'styled-components';
import { DesignSystem } from './designSystem';

// Sophisticated Animation Keyframes
export const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const fadeInScale = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

export const slideInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

export const slideInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

export const float = keyframes`
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
`;

export const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
`;

export const shimmer = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
`;

export const glow = keyframes`
  0%, 100% {
    box-shadow: 0 0 20px rgba(255, 255, 255, 0.1);
  }
  50% {
    box-shadow: 0 0 30px rgba(255, 255, 255, 0.2);
  }
`;

// Animated Components with Micro-interactions
interface AnimatedProps {
  delay?: number;
  duration?: number;
  stagger?: number;
}

export const FadeInContainer = styled.div<AnimatedProps>`
  ${props => css`
    animation: ${fadeIn} ${props.duration || 0.6}s ${DesignSystem.motion.easing.easeOut} 
               ${props.delay || 0}s both;
  `}
`;

export const FadeInUpContainer = styled.div<AnimatedProps>`
  ${props => css`
    animation: ${fadeInUp} ${props.duration || 0.8}s ${DesignSystem.motion.easing.easeOut} 
               ${props.delay || 0}s both;
  `}
`;

export const SlideInContainer = styled.div<AnimatedProps & { direction?: 'left' | 'right' }>`
  ${props => css`
    animation: ${props.direction === 'left' ? slideInLeft : slideInRight} 
               ${props.duration || 0.6}s ${DesignSystem.motion.easing.easeOut} 
               ${props.delay || 0}s both;
  `}
`;

// Hover Effects - Premium micro-interactions
export const HoverLift = styled.div`
  transition: all ${DesignSystem.motion.duration.normal} ${DesignSystem.motion.easing.ease};
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: ${DesignSystem.shadows.large};
  }
`;

export const HoverScale = styled.div`
  transition: all ${DesignSystem.motion.duration.normal} ${DesignSystem.motion.easing.bounce};
  
  &:hover {
    transform: scale(1.05);
  }
`;

export const HoverGlow = styled.div`
  transition: all ${DesignSystem.motion.duration.normal} ${DesignSystem.motion.easing.ease};
  
  &:hover {
    box-shadow: ${DesignSystem.shadows.glow};
  }
`;

// Loading and Progress Components
export const ShimmerLoader = styled.div`
  width: 100%;
  height: 20px;
  background: linear-gradient(
    90deg,
    #f0f0f0 0px,
    rgba(229, 229, 229, 0.8) 40px,
    #f0f0f0 80px
  );
  background-size: 200px;
  border-radius: ${DesignSystem.borderRadius.md};
  ${css`
    animation: ${shimmer} 1.2s ease-in-out infinite;
  `}
`;

export const PulseLoader = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${DesignSystem.colors.primary[600]};
  ${css`
    animation: ${pulse} 1.5s ease-in-out infinite;
  `}
`;

export const FloatingElement = styled.div<{ amplitude?: number }>`
  ${css`
    animation: ${float} 3s ease-in-out infinite;
  `}
  
  ${props => props.amplitude && css`
    @keyframes floatCustom {
      0%, 100% { transform: translateY(0); }
      50% { transform: translateY(-${props.amplitude}px); }
    }
    animation: floatCustom 3s ease-in-out infinite;
  `}
`;

// Progress Indicators
export const ProgressBar = styled.div<{ progress: number; animated?: boolean }>`
  width: 100%;
  height: 8px;
  background: ${DesignSystem.colors.neutral.gray[200]};
  border-radius: ${DesignSystem.borderRadius.full};
  overflow: hidden;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: ${props => props.progress}%;
    background: linear-gradient(
      90deg,
      ${DesignSystem.colors.primary[500]},
      ${DesignSystem.colors.primary[600]}
    );
    border-radius: ${DesignSystem.borderRadius.full};
    transition: width ${DesignSystem.motion.duration.slow} ${DesignSystem.motion.easing.easeOut};
    
    ${props => props.animated && css`
      background: linear-gradient(
        90deg,
        ${DesignSystem.colors.primary[400]},
        ${DesignSystem.colors.primary[600]},
        ${DesignSystem.colors.primary[400]}
      );
      background-size: 200% 100%;
      
      @keyframes progressSlide {
        0% { background-position: 200% 0; }
        100% { background-position: -200% 0; }
      }
      animation: progressSlide 2s ease-in-out infinite;
    `}
  }
`;

// Sophisticated Scroll Animations
export const ScrollReveal = styled.div<{ threshold?: number }>`
  opacity: 0;
  transform: translateY(50px);
  transition: all 0.8s ${DesignSystem.motion.easing.easeOut};
  
  &.revealed {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Parallax Component
export const ParallaxContainer = styled.div<{ speed?: number }>`
  will-change: transform;
  transition: transform 0.1s ease-out;
`;

// Staggered Animation Container
export const StaggerContainer = styled.div<{ staggerDelay?: number }>`
  & > * {
    opacity: 0;
    transform: translateY(30px);
    ${css`
      animation: ${fadeInUp} 0.6s ${DesignSystem.motion.easing.easeOut} both;
    `}
  }
  
  ${props => {
    const delay = props.staggerDelay || 0.1;
    let staggerCss = '';
    for (let i = 1; i <= 20; i++) {
      staggerCss += `
        & > *:nth-child(${i}) {
          animation-delay: ${delay * (i - 1)}s;
        }
      `;
    }
    return css`${staggerCss}`;
  }}
`;

// Interactive Elements
export const InteractiveCard = styled.div`
  position: relative;
  transition: all ${DesignSystem.motion.duration.normal} ${DesignSystem.motion.easing.ease};
  border-radius: ${DesignSystem.borderRadius['2xl']};
  overflow: hidden;
  cursor: pointer;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      45deg,
      transparent 30%,
      rgba(255, 255, 255, 0.1) 50%,
      transparent 70%
    );
    transform: translateX(-100%);
    transition: transform ${DesignSystem.motion.duration.slow} ${DesignSystem.motion.easing.ease};
    pointer-events: none;
  }
  
  &:hover {
    transform: translateY(-8px) scale(1.02);
    box-shadow: ${DesignSystem.shadows.luxe};
    
    &::before {
      transform: translateX(100%);
    }
  }
`;

// Notification and Toast Animations
export const slideInFromTop = keyframes`
  from {
    opacity: 0;
    transform: translateY(-100%);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const slideOutToTop = keyframes`
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(-100%);
  }
`;

export const NotificationContainer = styled.div<{ isVisible: boolean }>`
  ${props => css`
    animation: ${props.isVisible ? slideInFromTop : slideOutToTop} 
               ${DesignSystem.motion.duration.normal} ${DesignSystem.motion.easing.easeOut};
  `}
`;

// Utility function for scroll-triggered animations
export const useScrollReveal = () => {
  return {
    threshold: 0.1,
    rootMargin: '0px 0px -100px 0px',
  };
};

// Entrance Animation Hook
export const useEntranceAnimation = (delay: number = 0) => {
  return css`
    animation: ${fadeInUp} 0.8s ${DesignSystem.motion.easing.easeOut} ${delay}s both;
  `;
};